import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Ajouter le token d'authentification si disponible
    const token = this.getAuthToken();
    if (token && !this.isAuthRequest(request)) {
      request = this.addTokenToRequest(request, token);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Si erreur 401 et que ce n'est pas déjà une requête de refresh
        if (error.status === 401 && !this.isAuthRequest(request)) {
          return this.handle401Error(request, next);
        }

        // Si erreur 403, rediriger vers la page de connexion
        if (error.status === 403) {
          this.logout();
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Gère les erreurs 401 en tentant de rafraîchir le token
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        return this.refreshAuthToken().pipe(
          switchMap((token: any) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(token.access_token);
            this.setAuthToken(token.access_token);
            return next.handle(this.addTokenToRequest(request, token.access_token));
          }),
          catchError((error) => {
            this.isRefreshing = false;
            this.logout();
            return throwError(() => error);
          })
        );
      } else {
        this.logout();
        return throwError(() => new Error('No refresh token available'));
      }
    } else {
      // Si on est déjà en train de rafraîchir, attendre le nouveau token
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(jwt => {
          return next.handle(this.addTokenToRequest(request, jwt));
        })
      );
    }
  }

  /**
   * Ajoute le token à la requête
   */
  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  /**
   * Vérifie si c'est une requête d'authentification
   */
  private isAuthRequest(request: HttpRequest<any>): boolean {
    return request.url.includes('/auth/login') || 
           request.url.includes('/auth/register') || 
           request.url.includes('/auth/refresh');
  }

  /**
   * Récupère le token d'authentification
   */
  private getAuthToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return localStorage.getItem('access_token');
  }

  /**
   * Récupère le token de rafraîchissement
   */
  private getRefreshToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return localStorage.getItem('refresh_token');
  }

  /**
   * Sauvegarde le token d'authentification
   */
  private setAuthToken(token: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('access_token', token);
    }
  }

  /**
   * Rafraîchit le token d'authentification
   */
  private refreshAuthToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }

    // Ici, vous devrez implémenter l'appel API pour rafraîchir le token
    // Pour l'instant, on simule avec un Observable
    return new Observable(observer => {
      // Simulation d'un appel API
      setTimeout(() => {
        observer.error('Refresh token expired');
      }, 1000);
    });
  }

  /**
   * Déconnecte l'utilisateur et redirige vers la page de connexion
   */
  private logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
    this.router.navigate(['/auth/login']);
  }
}
